import "./simple-pdf-tool.css";
import "./ocr-pdf.css";
import { openBrowserPdf, pdfErrorMessage, renderPageBlob } from "./pdf-browser-core.js";
import { createOperationResult } from "./operation-result.js";
import {
  OCR_PDF_LIMITS, createOcrSearchElements, hasSearchableText,
  searchablePdfName, selectedOcrLanguages
} from "./ocr-pdf-core.js";

const root = document.querySelector("[data-ocr-pdf]");

if (root) {
  const ids = [
    "ocrStatus", "ocrUploadPanel", "ocrDropZone", "chooseOcrPdf", "ocrFileInput", "ocrWorkspace",
    "ocrFileName", "ocrPageCount", "replaceOcrPdf", "ocrPreview", "ocrLanguage", "ocrIncludeEnglish",
    "ocrSkipSearchable", "runPdfOcr", "cancelPdfOcr", "ocrProgress", "ocrProgressBar"
  ];
  const elements = Object.fromEntries(ids.map(id => [id, document.getElementById(id)]));
  const state = {
    document: null,
    bytes: null,
    fileName: "document.pdf",
    pageCount: 0,
    busy: false,
    cancelling: false,
    previewUrl: null,
    ocrModule: null,
    worker: null,
    workerRequests: new Map(),
    currentPage: 0
  };
  const operationResult = createOperationResult(root, elements.ocrWorkspace);

  function showStatus(message = "") {
    elements.ocrStatus.textContent = message;
    elements.ocrStatus.hidden = !message;
  }

  function updateControls() {
    elements.replaceOcrPdf.disabled = state.busy;
    elements.ocrLanguage.disabled = state.busy;
    elements.ocrIncludeEnglish.disabled = state.busy || elements.ocrLanguage.value === "eng";
    elements.ocrSkipSearchable.disabled = state.busy;
    elements.runPdfOcr.disabled = state.busy || !state.document;
    elements.cancelPdfOcr.hidden = !state.busy;
  }

  function setProgress(message, value = null) {
    elements.ocrProgress.textContent = message;
    elements.ocrProgress.hidden = !message;
    elements.ocrProgressBar.hidden = value == null;
    if (value != null) elements.ocrProgressBar.value = Math.max(0, Math.min(1, value));
  }

  async function updatePreview() {
    const page = await state.document.getPage(1);
    try {
      const blob = await renderPageBlob(page, { dpi: 110, maxWidth: 560, maxHeight: 560 });
      if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
      state.previewUrl = URL.createObjectURL(blob);
      elements.ocrPreview.src = state.previewUrl;
    } finally {
      page.cleanup();
    }
  }

  async function loadFile(file) {
    if (state.busy) return;
    showStatus();
    state.busy = true;
    updateControls();
    let loaded = null;
    try {
      loaded = await openBrowserPdf(file, OCR_PDF_LIMITS);
      await releaseDocument();
      state.document = loaded.document;
      state.bytes = loaded.bytes;
      state.fileName = loaded.fileName;
      state.pageCount = loaded.pageCount;
      loaded = null;
      elements.ocrFileName.textContent = state.fileName;
      elements.ocrPageCount.textContent = String(state.pageCount);
      elements.ocrUploadPanel.hidden = true;
      elements.ocrWorkspace.hidden = false;
      await updatePreview();
    } catch (error) {
      if (loaded?.document) await loaded.document.destroy();
      showStatus(pdfErrorMessage(error));
    } finally {
      state.busy = false;
      elements.ocrFileInput.value = "";
      updateControls();
    }
  }

  async function recognize() {
    if (state.busy || !state.document || !state.bytes) return;
    showStatus();
    state.busy = true;
    state.cancelling = false;
    elements.ocrProgressBar.removeAttribute("value");
    setProgress("Loading OCR language data…", 0);
    updateControls();
    let session = null;
    try {
      state.ocrModule ||= await import("./pdf-editor-ocr.js");
      const languages = selectedOcrLanguages(elements.ocrLanguage.value, elements.ocrIncludeEnglish.checked);
      session = await state.ocrModule.createPdfOcrSession(languages, updateRecognitionProgress);
      const searchElements = [];
      let skippedPages = 0;
      let recognizedPages = 0;

      for (let pageNumber = 1; pageNumber <= state.pageCount; pageNumber += 1) {
        throwIfCancelled();
        state.currentPage = pageNumber;
        setProgress(`Checking page ${pageNumber} of ${state.pageCount}…`, (pageNumber - 1) / state.pageCount);
        const page = await state.document.getPage(pageNumber);
        try {
          if (elements.ocrSkipSearchable.checked) {
            const text = await page.getTextContent();
            if (hasSearchableText(text.items)) {
              skippedPages += 1;
              continue;
            }
          }
          const lines = await session.recognizePage(page, { layout: "lines" });
          if (lines.length) recognizedPages += 1;
          searchElements.push(...createOcrSearchElements(pageNumber, lines, searchElements.length));
        } finally {
          page.cleanup();
        }
      }

      await session.terminate();
      session = null;
      throwIfCancelled();

      if (!searchElements.length) {
        await releaseDocument();
        operationResult.show(skippedPages === state.pageCount
          ? {
              title: "This PDF is already searchable",
              message: "Every page already contains selectable text, so no OCR layer was added.",
              details: [`${skippedPages} page${skippedPages === 1 ? "" : "s"} checked`]
            }
          : {
              title: "No printed text was found",
              message: "OCR could not find enough printed text to create a searchable copy.",
              details: skippedPages ? [`${skippedPages} searchable page${skippedPages === 1 ? "" : "s"} skipped`] : []
            });
        return;
      }

      setProgress("Creating your searchable PDF…", .96);
      const output = await exportSearchablePdf(searchElements);
      await releaseDocument();
      const details = [
        `${recognizedPages} page${recognizedPages === 1 ? "" : "s"} recognized`,
        skippedPages ? `${skippedPages} searchable page${skippedPages === 1 ? "" : "s"} left unchanged` : null
      ].filter(Boolean);
      operationResult.show({
        blob: new Blob([output], { type: "application/pdf" }),
        fileName: searchablePdfName(state.fileName),
        message: "Searchable text was added while keeping the original page appearance.",
        details
      });
    } catch (error) {
      if (!state.cancelling && error?.code !== "OCR_CANCELLED") {
        showStatus(error?.message || "smallpdf.app could not recognize this PDF.");
      }
    } finally {
      if (session) await session.terminate();
      releaseWorker();
      state.busy = false;
      state.cancelling = false;
      state.currentPage = 0;
      setProgress();
      updateControls();
    }
  }

  function updateRecognitionProgress(progress) {
    const pageProgress = Math.max(0, Math.min(1, Number(progress?.progress) || 0));
    const overall = state.pageCount
      ? ((Math.max(1, state.currentPage) - 1) + pageProgress) / state.pageCount
      : pageProgress;
    setProgress(`${progress?.status || "Recognizing text"} · page ${Math.max(1, state.currentPage)} of ${state.pageCount}`, overall);
  }

  function throwIfCancelled() {
    if (!state.cancelling) return;
    const error = new Error("OCR_CANCELLED");
    error.code = "OCR_CANCELLED";
    throw error;
  }

  async function cancel() {
    if (!state.busy || state.cancelling) return;
    state.cancelling = true;
    setProgress("Cancelling…");
    await state.ocrModule?.cancelPdfOcr();
    releaseWorker();
  }

  async function exportSearchablePdf(searchElements) {
    releaseWorker();
    const worker = new Worker("/dist/pdf-editor-worker.js", { type: "module" });
    state.worker = worker;
    worker.addEventListener("message", handleWorkerMessage);
    worker.addEventListener("error", () => failWorker("The PDF processor stopped unexpectedly."));
    const source = state.bytes.slice();
    await requestWorker("initialize", { buffer: source.buffer }, [source.buffer]);
    const response = await requestWorker("export", {
      elements: searchElements,
      assets: [],
      producer: "smallpdf.app OCR PDF"
    });
    return new Uint8Array(response.buffer);
  }

  function requestWorker(action, payload = {}, transfer = []) {
    if (!state.worker) return Promise.reject(new Error("The PDF processor is unavailable."));
    return new Promise((resolve, reject) => {
      const id = crypto.randomUUID();
      const timeout = window.setTimeout(() => {
        state.workerRequests.delete(id);
        reject(new Error("Creating the searchable PDF took too long."));
      }, 300_000);
      state.workerRequests.set(id, { resolve, reject, timeout });
      state.worker.postMessage({ id, action, ...payload }, transfer);
    });
  }

  function handleWorkerMessage(event) {
    const request = state.workerRequests.get(event.data?.id);
    if (!request) return;
    if (event.data.progress) return;
    window.clearTimeout(request.timeout);
    state.workerRequests.delete(event.data.id);
    if (event.data.ok) request.resolve(event.data);
    else request.reject(new Error(workerErrorMessage(event.data.code)));
  }

  function workerErrorMessage(code) {
    if (code === "EDITOR_FONT_LOAD_FAILED") return "A language font could not be loaded. Check your connection and try again.";
    return "The searchable PDF could not be created.";
  }

  function failWorker(message) {
    for (const request of state.workerRequests.values()) {
      window.clearTimeout(request.timeout);
      request.reject(new Error(message));
    }
    state.workerRequests.clear();
    state.worker?.terminate();
    state.worker = null;
  }

  function releaseWorker() {
    failWorker("The PDF processor was stopped.");
  }

  async function releaseDocument() {
    if (state.previewUrl) URL.revokeObjectURL(state.previewUrl);
    state.previewUrl = null;
    if (state.document) await state.document.destroy();
    state.document = null;
    state.bytes = null;
  }

  elements.chooseOcrPdf.addEventListener("click", event => {
    event.stopPropagation();
    elements.ocrFileInput.click();
  });
  elements.replaceOcrPdf.addEventListener("click", () => elements.ocrFileInput.click());
  elements.ocrFileInput.addEventListener("change", () => loadFile(elements.ocrFileInput.files?.[0]));
  elements.runPdfOcr.addEventListener("click", recognize);
  elements.cancelPdfOcr.addEventListener("click", cancel);
  elements.ocrLanguage.addEventListener("change", () => {
    if (elements.ocrLanguage.value === "eng") elements.ocrIncludeEnglish.checked = false;
    updateControls();
  });
  elements.ocrDropZone.addEventListener("click", event => {
    if (!event.target.closest("button")) elements.ocrFileInput.click();
  });
  elements.ocrDropZone.addEventListener("keydown", event => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      elements.ocrFileInput.click();
    }
  });
  elements.ocrDropZone.addEventListener("dragover", event => {
    event.preventDefault();
    elements.ocrDropZone.classList.add("is-dragging");
  });
  elements.ocrDropZone.addEventListener("dragleave", () => elements.ocrDropZone.classList.remove("is-dragging"));
  elements.ocrDropZone.addEventListener("drop", event => {
    event.preventDefault();
    elements.ocrDropZone.classList.remove("is-dragging");
    loadFile(event.dataTransfer.files?.[0]);
  });
  window.addEventListener("beforeunload", () => {
    operationResult.dispose();
    state.ocrModule?.cancelPdfOcr();
    releaseWorker();
    releaseDocument();
  });
  updateControls();
}
